// React hooks
import { createContext, useContext, useState, useEffect } from "react";
// Skybase hooks
import { API_BASE_URL } from "../config";
// External hooks/components
import { useAuth as useClerkAuth } from "@clerk/react";


// Create React Context
const AuthContext = createContext();


// ============
// AuthProvider  – provides GC related to Skybase User Authenication.
// ============
export default function AuthProvider({ children }) {

    // init hooks
    const { getToken, isSignedIn, isLoaded } = useClerkAuth();

    // ==========
    // Sleep(int) – helper function that provides a basic async sleep delay
    // ==========
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // ============
    // isSubscribed (bool) – IFF a Clerk User isSignedIn, immediately checks whether the Clerk User has an active Stripe Subscription.
    // ⚠️ NOTE: This var is NOT used for any product Authorization. It's exclusively used as a permissive traffic light to allow/disallow FE product fetches().
    // ============
    const [isSubscribed, setIsSubscribed] = useState(() => {
        return (isSignedIn ? checkIsSubscribed() : false)
    });
    // onChange (@Clerk isSignedIn) --> immediately check if the CurrentUser 'isSubscirbed'
    useEffect(() => {
        
        // Exit Condition: Immediately exit if Clerk User !isSignedIn
        if (!isSignedIn) { return };

        // Otherwise, check for valid Subscription
        checkIsSubscribed();

    }, [isSignedIn])
    // checkIsSubscribed() --> async. fn to check if a Postgres CurrentUser has an active Skybase Stripe Subscription
    async function checkIsSubscribed() {
        
        // Exit: Never fetch() if <Clerk JWT> DNE
        const token = await getToken();
        if (!token) { return };
        
        // `GET /users/me/is-subscribed`
        const url = new URL(`${API_BASE_URL}/api/v1/users/me/is-subscribed`);
        const res = await fetch(url,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        // error-handling
        if (!res.ok) { throw new Error(`SKYBASE (client) – HTTP ${res.status}: Unable to check isSubscribed of CurrentUser.`)};

        // extract json --> update JSON state var (triggers re-render)
        const res_json = await res.json();
        setIsSubscribed(res_json);
        
        // return BOOLEAN --> "Does the CurrentUser have an active subscription?"
        return res_json;
    }


    // ================
    // ACTIVE USER JSON – The JSON Postgres data of the Axum CurrentUser.
    // ================
    // state vars
    const [activeUserJson, setActiveUserJson] = useState(null);
    // onChange ('isSignedIn') --> immediately runs fetchUserJson() if a Clerk User isSignedIn.  
    useEffect(() => {
        if (isSignedIn) { fetchUserJson(); }
    }, [isSignedIn]);
    // fetchUserJson() --> async fn. that gets the JSON of the Axum CurrentUser.
    async function fetchUserJson() {
        while (true) {
            try {
            
            // Exit: Never fetch() if <Clerk JWT> DNE
            const token = await getToken();
            if (!token) { break };

            // `GET /users/current`
            const url = new URL(`${API_BASE_URL}/api/v1/users/current`);
            const response = await fetch(url,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // extract JSON
            const current_user_json = await response.json();

            // ⚠️ developer debugging (NOTE: Never reveal full personal User information)
            // console.log(`\nSKYBASE (client): Active User`);
            // console.log(`ID: ${current_user_json.id}`);
            // console.log(`Name: ${current_user_json.first_name} ${current_user_json.last_name}`);

            // update JSON (triggers re-render)
            setActiveUserJson(current_user_json);

            // SUCCESS → exit loop
            break;

            } catch (err) {
                console.error("Fetch failed. Retrying in 3 seconds...", err);

                // wait 3 seconds before retrying
                await sleep(3000);
            }
        }   
    }

    // ===============================================
    // Clear localStorage Values when User !isSignedIn
    // ===============================================
    useEffect(() => {
        // Do nothing if CurrentUser isLoading
        if (!isLoaded) return;

        // Delete all sensitive CurrentUser data in localStorage cache if CurrentUser !isSignedIn
        if (!isSignedIn) {
            localStorage.removeItem("activeEngineBookmarkJson");
            localStorage.removeItem("activeEngineSearchEvents");
            localStorage.removeItem("activeQuery");
            localStorage.removeItem("rankedQueryResults");
            localStorage.removeItem("selectedSearchResultJson");
            localStorage.removeItem("activeEngineId");
            localStorage.removeItem("activeEngineJson");
            localStorage.removeItem("activeEngine");
            localStorage.removeItem("pinnedEngineJson");

            setActiveUserJson(null);
        }
    })

   
    // JSX Component – React Provider
    return(
        <AuthContext.Provider
        value={{
            // SUBSCRIPTION STATUS
            isSubscribed,           // Boolean
            checkIsSubscribed,      // async fn()
            // ACTIVE USER
            activeUserJson,         // JSON
            fetchUserJson,          // async fn()
        }}
        >
            {children}
        </AuthContext.Provider>
    );
}



// Create the Skybase 'useAuth' hook
export function useAuth() {
    return useContext(AuthContext);
}

